import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { OrgPicker } from '../components/OrgPicker.tsx';
import { MatrixScrollBox } from '../snapshot/components/MatrixScrollBox.tsx';
import { StatusBanner, type StatusKind } from '../snapshot/components/StatusBanner.tsx';
import { useBusinessFunctions } from '../view-models/business-functions.ts';
import { useDerivedContexts } from '../view-models/derived-contexts.ts';
import { useRewritePlatformMap } from '../view-models/platform-map.ts';

import { PlatformMapGrid } from './PlatformMapGrid.tsx';
import type { SettingsOutletContext } from './Settings.tsx';
import { usePlatformMapDraft } from './usePlatformMapDraft.ts';

/**
 * /setup/platform-map — the platform-map area of the setup flow.
 *
 * Owners edit the org's framework (contexts × business functions)
 * against a live preview: hover labels to rename/delete, click a skill
 * count to expand and edit skills, add derivedContexts / business
 * functions, and toggle intersections by clicking cells. All edits stay
 * in the in-memory draft until "Save & Rerun".
 *
 * PR2 ships the editing surface; wiring "Save & Rerun" to the
 * genesis-rewrite + re-derive backend lands in PR3.
 */
/** Save & Rerun result, shown in the shared onboarding-style banner. */
interface SaveNote {
  kind: StatusKind;
  title: string;
  body: string;
}

export function SetupPlatformMap() {
  const { orgSlug, role } = useOutletContext<SettingsOutletContext>();
  const comps = useDerivedContexts(orgSlug);
  const fns = useBusinessFunctions(orgSlug);
  const editable = role === 'admin';

  const isPending = comps.isPending || fns.isPending;
  const error = comps.error ?? fns.error;
  const derivedContexts = comps.data ?? [];
  const functions = fns.data ?? [];

  const draft = usePlatformMapDraft(derivedContexts, functions);
  const rewrite = useRewritePlatformMap(orgSlug);
  const [note, setNote] = useState<SaveNote | null>(null);
  // "Group functions" view toggle — cluster child functions under their
  // parent (nested) vs. show them flat. A pure view concern, so it's
  // available in the read-only preview too.
  const [grouped, setGrouped] = useState(false);

  const hasMap = derivedContexts.length > 0 && functions.length > 0;

  const onSave = () => {
    setNote(null);
    rewrite.mutate(draft.framework, {
      onSuccess: (res) => {
        // Clear the dirty draft; the invalidated queries re-seed it.
        draft.reset();
        setNote(
          res.status === 'backfill-spawned'
            ? {
                kind: 'success',
                title: 'Saved',
                body: 'New commits were detected — a backfill is running and will re-derive your history through the new framework shortly.',
              }
            : {
                kind: 'success',
                title: 'Saved and re-ran',
                body: `Re-derived ${res.derive?.scores_inserted ?? 0} scores across ${res.derive?.engineers ?? 0} engineers through the new framework.`,
              },
        );
      },
      onError: (err) =>
        setNote({
          kind: 'error',
          title: 'Couldn’t save',
          body: err instanceof Error ? err.message : String(err),
        }),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1 className="settings-h1">Setup platform map</h1>
          <p className="settings-subtitle">
            {editable
              ? 'Fine-tune the business functions and contexts your matrices are built from. Changes preview live and only apply when you Save & Rerun.'
              : 'Preview the business functions and contexts your matrices are built from.'}
          </p>
        </div>
        {hasMap ? (
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'flex-start' }}>
            <button
              type="button"
              className="snap-toolbar__btn snap-toolbar__btn--ghost"
              aria-pressed={grouped}
              onClick={() => setGrouped((g) => !g)}
              title="Cluster each business function under its parent"
            >
              {grouped ? 'Ungroup functions' : 'Group functions'}
            </button>
            {editable && draft.dirty && !rewrite.isPending ? (
              <button
                type="button"
                className="snap-toolbar__btn snap-toolbar__btn--ghost"
                onClick={() => {
                  draft.reset();
                  setNote(null);
                }}
              >
                Reset
              </button>
            ) : null}
            {editable ? (
              <button
                type="button"
                className="snap-toolbar__btn"
                disabled={!draft.dirty || rewrite.isPending}
                onClick={onSave}
              >
                {rewrite.isPending ? 'Saving…' : 'Save & Rerun'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <OrgPicker />

      {note ? (
        <StatusBanner
          kind={note.kind}
          title={note.title}
          body={note.body}
          onDismiss={() => setNote(null)}
        />
      ) : null}

      {isPending ? (
        <p className="muted">Loading…</p>
      ) : error ? (
        <p className="muted">Couldn’t load the platform map: {String(error)}</p>
      ) : !hasMap ? (
        <p className="muted">
          This org has no platform map yet. Connect a repo and run onboarding to
          generate one.
        </p>
      ) : (
        <MatrixScrollBox>
          <PlatformMapGrid draft={draft} editable={editable} grouped={grouped} />
        </MatrixScrollBox>
      )}
    </div>
  );
}
